// src/components/Form/schedulemaster.jsx
// FULLY DETAILED: Shift Master page
// Manages "shift_master" table via /api/schedules endpoint
// UPDATED: Added "height: 100vh" and "overflowY: auto" to main container to ensure full page scrolling.
// Data is ALWAYS stored as "HH:MM AM/PM" (12h format) for backend consistency.
// The UI (both Inputs and Table) handles conversions dynamically based on toggle state.
import React, { useState, useEffect, useRef, useContext } from 'react';
import { UserContext } from '../../Context/UserContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';
import { FaArrowLeft, FaClock, FaSave, FaEdit, FaTrash, FaTimes, FaPlus, FaMoon, FaSun, FaExclamationTriangle, FaCalendarAlt, FaCheckCircle, FaBuilding, FaCog, FaCogs, FaMapMarkerAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import "./CreateCustomerPage.css";

// --- Helper Functions for Format Conversion ---
const to12h = (h, m) => {
  let hour = parseInt(h);
  const minute = m.toString().padStart(2, '0');
  const period = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  hour = hour ? hour : 12; // 0 becomes 12
  return `${hour.toString().padStart(2, '0')}:${minute} ${period}`;
};

const to24h = (h, m, p) => {
  let hour = parseInt(h);
  if (p === 'PM' && hour !== 12) hour += 12;
  if (p === 'AM' && hour === 12) hour = 0;
  return { h: hour.toString().padStart(2, '0'), m: m.toString().padStart(2, '0') };
};

// --- Custom Time Picker Component ---
const CustomDateTimePicker = ({ value, onChange, placeholder, type = 'datetime-local' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('days'); 
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) return parsed;
    }
    return new Date();
  });
  const [yearPage, setYearPage] = useState(() => Math.floor((currentDate.getFullYear()) / 12) * 12);

  const [popupDay, setPopupDay] = useState(() => value ? (new Date(value).getDate() || "") : "");
  const [popupMonth, setPopupMonth] = useState(() => value ? ((new Date(value).getMonth() + 1) || "") : "");
  const [popupYear, setPopupYear] = useState(() => value ? (new Date(value).getFullYear() || "") : "");
  
  const [popupTime, setPopupTime] = useState(() => {
    if (value && value.includes('T')) {
      return value.split('T')[1].substring(0, 5);
    }
    return "";
  });

  const [popupHour, setPopupHour] = useState(() => {
    if (value && value.includes('T')) {
      let h = parseInt(value.split('T')[1].split(':')[0], 10);
      return h % 12 || 12;
    }
    return 12;
  });
  const [popupMinute, setPopupMinute] = useState(() => {
    if (value && value.includes('T')) return parseInt(value.split('T')[1].split(':')[1], 10);
    return 0;
  });
  const [popupPeriod, setPopupPeriod] = useState(() => {
    if (value && value.includes('T')) return parseInt(value.split('T')[1].split(':')[0], 10) >= 12 ? 'PM' : 'AM';
    return 'AM';
  });

  useEffect(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) {
        setCurrentDate(parsed);
        setYearPage(Math.floor(parsed.getFullYear() / 12) * 12);
        setPopupDay(parsed.getDate() || "");
        setPopupMonth((parsed.getMonth() + 1) || "");
        setPopupYear(parsed.getFullYear() || "");
        if (value.includes('T')) {
          const tPart = value.split('T')[1];
          setPopupTime(tPart.substring(0, 5));
          let h = parseInt(tPart.split(':')[0], 10);
          setPopupHour(h % 12 || 12);
          setPopupMinute(parseInt(tPart.split(':')[1], 10));
          setPopupPeriod(h >= 12 ? 'PM' : 'AM');
        }
      }
    } else {
      setPopupDay(""); setPopupMonth(""); setPopupYear(""); setPopupTime("");
      setPopupHour(12); setPopupMinute(0); setPopupPeriod('AM');
    }
  }, [value]);

  const emitChange = (y, m, d, t) => {
    const formattedMonth = String(m).padStart(2, '0');
    const formattedDay = String(d).padStart(2, '0');
    if (type === 'datetime-local') {
      const timeStr = t || "00:00";
      onChange(`${y}-${formattedMonth}-${formattedDay}T${timeStr}`);
    } else {
      onChange(`${y}-${formattedMonth}-${formattedDay}`);
    }
  };

  const handleQuickChange = (inputType, val) => {
    if (inputType === 'day') setPopupDay(val);
    if (inputType === 'month') setPopupMonth(val);
    if (inputType === 'year') setPopupYear(val);

    const newDay = inputType === 'day' ? val : popupDay;
    const newMonth = inputType === 'month' ? val : popupMonth;
    const newYear = inputType === 'year' ? val : popupYear;

    const parsedYear = parseInt(newYear, 10);
    const parsedMonth = parseInt(newMonth, 10);
    const parsedDay = parseInt(newDay, 10);

    if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12 && !isNaN(parsedDay) && parsedDay >= 1 && parsedDay <= 31) {
      const newDateObj = new Date(parsedYear, parsedMonth - 1, parsedDay);
      if (!isNaN(newDateObj)) {
        setCurrentDate(newDateObj);
        setYearPage(Math.floor(parsedYear / 12) * 12);
        emitChange(parsedYear, parsedMonth, parsedDay, popupTime);
      }
    } else if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) {
      setCurrentDate(new Date(parsedYear, parsedMonth - 1, 1));
      setYearPage(Math.floor(parsedYear / 12) * 12);
    }
  };

  const handleTimeSelect = (tType, val) => {
    let newH = popupHour, newM = popupMinute, newP = popupPeriod;
    if (tType === 'h') newH = val;
    if (tType === 'm') newM = val;
    if (tType === 'p') newP = val;
    setPopupHour(newH); setPopupMinute(newM); setPopupPeriod(newP);
    
    let h24 = newH;
    if (newP === 'PM' && newH !== 12) h24 += 12;
    if (newP === 'AM' && newH === 12) h24 = 0;
    
    const timeStr = `${String(h24).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
    setPopupTime(timeStr);
    
    const dYear = popupYear || selectedYear || year;
    const dMonth = popupMonth || (selectedMonth !== null ? selectedMonth + 1 : month + 1);
    const dDay = popupDay || selectedDay;
    if (dYear && dMonth && dDay) {
      emitChange(dYear, dMonth, dDay, timeStr);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  
  let startOffset = firstDayOfMonth.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  let selectedYear = null, selectedMonth = null, selectedDay = null;
  if (value) {
    const parsed = new Date(value);
    if (!isNaN(parsed)) {
      selectedYear = parsed.getFullYear();
      selectedMonth = parsed.getMonth();
      selectedDay = parsed.getDate();
    }
  }

  const displayValue = value ? (type === 'datetime-local' ? value.replace('T', ' ') : value) : "";

  return (
    <div className="custom-datepicker-container" style={{ position: 'relative', width: '100%' }}>
      <div className="custom-datepicker-input" onClick={() => { setIsOpen(!isOpen); setView('days'); }} style={{ position: 'relative' }}>
        <input type="text" readOnly value={displayValue} placeholder={placeholder || (type === 'datetime-local' ? "YYYY-MM-DD HH:MM" : "YYYY-MM-DD")} style={{ cursor: 'pointer', background: '#F3F6FB', width: '100%', height: '44px', padding: '0 16px', borderRadius: '8px', border: '1px solid transparent', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }} />
        <FaCalendarAlt style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#604BE8', pointerEvents: 'none' }} />
      </div>

      {isOpen && (
        <>
          <div className="custom-datepicker-backdrop" onClick={() => setIsOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} />
          <div className="custom-datepicker-popup" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: type === 'datetime-local' ? '560px' : '320px', background: '#ffffff', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: '24px', zIndex: 1001, border: '1px solid #e2e8f0', animation: 'slideDown 0.2s ease', boxSizing: 'border-box', display: 'flex', gap: '24px' }}>
            
            {/* Left Side: Date Calendar */}
            <div style={{ width: '100%', flex: 1 }}>
              <div className="popup-quick-inputs-container" style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '0 0 24px 0', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Quick Enter Date</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}><span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Day</span><input type="number" value={popupDay} onChange={(e) => handleQuickChange('day', e.target.value)} placeholder="DD" min="1" max="31" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} /></div>
                  <div style={{ flex: 1 }}><span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Month</span><input type="number" value={popupMonth} onChange={(e) => handleQuickChange('month', e.target.value)} placeholder="MM" min="1" max="12" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} /></div>
                  <div style={{ flex: 1.5 }}><span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Year</span><input type="number" value={popupYear} onChange={(e) => handleQuickChange('year', e.target.value)} placeholder="YYYY" min="1000" max="9999" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} /></div>
                </div>
              </div>

              {view === 'days' && (
                <>
                  <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(year, month - 1, 1)); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronLeft /></button>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span onClick={(e) => { e.stopPropagation(); setView('months'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }}>{monthNames[month]}</span>
                      <span onClick={(e) => { e.stopPropagation(); setYearPage(Math.floor(year / 12) * 12); setView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }}>{year}</span>
                    </div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(year, month + 1, 1)); }} style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronRight /></button>
                  </div>
                  <div className="custom-datepicker-weekdays" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '16px' }}>
                    {["Mo", "Tu", "We", "Th", "Fri", "Sa", "Su"].map((w, i) => <span key={i} style={{ fontSize: '13px', fontWeight: '700', color: '#1B1B29' }}>{w}</span>)}
                  </div>
                  <div className="custom-datepicker-days" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px 4px' }}>
                    {Array.from({ length: startOffset }).map((_, i) => <div key={`empty-${i}`} />)}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const isSelected = selectedYear === year && selectedMonth === month && selectedDay === day;
                      return (
                        <button key={day} type="button" onClick={(e) => { e.stopPropagation(); emitChange(year, month + 1, day, popupTime); if(type !== 'datetime-local') setIsOpen(false); setView('days'); }} style={{ height: '36px', width: '100%', border: 'none', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: isSelected ? '700' : '600', cursor: 'pointer', background: isSelected ? '#1B65F2' : 'transparent', color: isSelected ? '#ffffff' : '#1B1B29' }}>{day}</button>
                      );
                    })}
                  </div>
                </>
              )}

              {view === 'months' && (
                <div>
                  <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setView('days'); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaChevronLeft /></button>
                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29' }}>Select Month</span>
                    <div style={{ width: '36px' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {monthNames.map((mName, index) => (
                      <button key={mName} type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(year, index, 1)); setView('days'); }} style={{ height: '42px', border: 'none', borderRadius: '10px', background: index === month ? '#1B65F2' : '#F3F6FB', color: index === month ? '#ffffff' : '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>{mName.substring(0, 3)}</button>
                    ))}
                  </div>
                </div>
              )}

              {view === 'years' && (
                <div>
                  <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage - 12); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaChevronLeft /></button>
                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setView('days'); }}>{yearPage} - {yearPage + 11}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage + 12); }} style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaChevronRight /></button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {Array.from({ length: 12 }).map((_, i) => {
                      const y = yearPage + i;
                      return (
                        <button key={y} type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(y, month, 1)); setView('days'); }} style={{ height: '42px', border: 'none', borderRadius: '10px', background: y === year ? '#1B65F2' : '#F3F6FB', color: y === year ? '#ffffff' : '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>{y}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Side: Custom Time Picker */}
            {type === 'datetime-local' && (
              <div style={{ width: '220px', borderLeft: '1px solid #e2e8f0', paddingLeft: '24px', display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Select Time</span>
                <div style={{ display: 'flex', flex: 1, gap: '4px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                  {/* Hours */}
                  <div style={{ flex: 1, overflowY: 'auto', height: '240px', borderRight: '1px solid #e2e8f0' }}>
                    {Array.from({ length: 12 }).map((_, i) => {
                      const h = i + 1;
                      const isSelected = popupHour === h;
                      return (
                        <div key={h} onClick={() => handleTimeSelect('h', h)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                          {String(h).padStart(2, '0')}
                        </div>
                      );
                    })}
                  </div>
                  {/* Minutes */}
                  <div style={{ flex: 1, overflowY: 'auto', height: '240px', borderRight: '1px solid #e2e8f0' }}>
                    {Array.from({ length: 60 }).map((_, i) => {
                      const m = i;
                      const isSelected = popupMinute === m;
                      return (
                        <div key={m} onClick={() => handleTimeSelect('m', m)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                          {String(m).padStart(2, '0')}
                        </div>
                      );
                    })}
                  </div>
                  {/* AM/PM */}
                  <div style={{ flex: 1, overflowY: 'auto', height: '240px' }}>
                    {['AM', 'PM'].map(p => {
                      const isSelected = popupPeriod === p;
                      return (
                        <div key={p} onClick={() => handleTimeSelect('p', p)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                          {p}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ background: '#F3F6FB', padding: '8px 12px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', flex: 1, marginRight: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{String(popupHour).padStart(2, '0')}:{String(popupMinute).padStart(2, '0')} {popupPeriod}</span>
                    <FaClock style={{ color: '#64748b' }} />
                  </div>
                  <button type="button" onClick={() => setIsOpen(false)} style={{ padding: '8px 20px', background: '#604BE8', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>OK</button>
                </div>
              </div>
            )}
            
          </div>
        </>
      )}
    </div>
  );
};


const CustomTimePicker = ({ value, onChange, format24, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popupTime, setPopupTime] = useState(value || "");
  const [popupHour, setPopupHour] = useState(() => {
    if (value) {
      const match = value.match(/^(\d{1,2})[:.]?(\d{0,2})\s*([AP]M)?$/i);
      if (match) {
        let h = parseInt(match[1], 10);
        if (format24) return h;
        return h % 12 || 12;
      }
    }
    return 12;
  });
  const [popupMinute, setPopupMinute] = useState(() => {
    if (value) {
      const match = value.match(/^(\d{1,2})[:.]?(\d{0,2})\s*([AP]M)?$/i);
      if (match) return parseInt(match[2] || 0, 10);
    }
    return 0;
  });
  const [popupPeriod, setPopupPeriod] = useState(() => {
    if (value && value.includes('PM')) return 'PM';
    return 'AM';
  });

  useEffect(() => {
    if (value) {
      const match = value.match(/^(\d{1,2})[:.]?(\d{0,2})\s*([AP]M)?$/i);
      if (match) {
        let h = parseInt(match[1], 10);
        let m = parseInt(match[2] || 0, 10);
        let p = match[3] ? match[3].toUpperCase() : 'AM';
        setPopupHour(h);
        setPopupMinute(m);
        setPopupPeriod(p);
      }
    }
  }, [value]);

  const handleTimeSelect = (tType, val) => {
    let newH = popupHour, newM = popupMinute, newP = popupPeriod;
    if (tType === 'h') newH = val;
    if (tType === 'm') newM = val;
    if (tType === 'p') newP = val;
    setPopupHour(newH); setPopupMinute(newM); setPopupPeriod(newP);
  };

  const confirmTime = () => {
    let timeStr = "";
    if (format24) {
      timeStr = `${String(popupHour).padStart(2, '0')}:${String(popupMinute).padStart(2, '0')}`;
    } else {
      timeStr = `${String(popupHour).padStart(2, '0')}:${String(popupMinute).padStart(2, '0')} ${popupPeriod}`;
    }
    setPopupTime(timeStr);
    onChange(timeStr);
    setIsOpen(false);
  };

  return (
    <div className="custom-datepicker-container" style={{ position: 'relative', width: '100%' }}>
      <div className="custom-datepicker-input" onClick={() => setIsOpen(!isOpen)} style={{ position: 'relative' }}>
        <input type="text" readOnly value={value || ""} placeholder={placeholder || "HH:MM"} style={{ cursor: 'pointer', background: '#F3F6FB', width: '100%', height: '44px', padding: '0 16px', borderRadius: '8px', border: '1px solid transparent', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }} />
        <FaClock style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#604BE8', pointerEvents: 'none' }} />
      </div>

      {isOpen && (
        <>
          <div className="custom-datepicker-backdrop" onClick={() => setIsOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} />
          <div className="custom-datepicker-popup" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: '260px', background: '#ffffff', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: '24px', zIndex: 1001, border: '1px solid #e2e8f0', animation: 'slideDown 0.2s ease', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '12px', display: 'block' }}>Select Time</span>
            <div style={{ display: 'flex', gap: '4px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', height: '200px' }}>
              <div style={{ flex: 1, overflowY: 'auto', borderRight: '1px solid #e2e8f0' }}>
                {Array.from({ length: format24 ? 24 : 12 }).map((_, i) => {
                  const h = format24 ? i : i + 1;
                  const isSelected = popupHour === h;
                  return (
                    <div key={h} onClick={() => handleTimeSelect('h', h)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                      {String(h).padStart(2, '0')}
                    </div>
                  );
                })}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', borderRight: !format24 ? '1px solid #e2e8f0' : 'none' }}>
                {Array.from({ length: 60 }).map((_, i) => {
                  const m = i;
                  const isSelected = popupMinute === m;
                  return (
                    <div key={m} onClick={() => handleTimeSelect('m', m)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                      {String(m).padStart(2, '0')}
                    </div>
                  );
                })}
              </div>
              {!format24 && (
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {['AM', 'PM'].map(p => {
                    const isSelected = popupPeriod === p;
                    return (
                      <div key={p} onClick={() => handleTimeSelect('p', p)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                        {p}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button type="button" onClick={confirmTime} style={{ padding: '8px 20px', width: '100%', background: '#604BE8', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>OK</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// --- Main Component ---
const ScheduleMaster = ({ permissions }) => {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  // REMOVED: departments state
  // Using existing 'shifts' state for dropdowns as well.


  // Format Toggle State
  const [use24Hour, setUse24Hour] = useState(false); // Default to 12h

  // --- Metadata & DocType State ---
  const [metadata, setMetadata] = useState(null);
  const [doctypeFields, setDoctypeFields] = useState([]);

  const [formData, setFormData] = useState({
    schedule_name: '',
    time_slots: [{ start_time: '', end_time: '', is_overnight: false }],
    working_days: [],
    start_date: '',
    end_date: '',
    special_days: [],
    is_active: true
  });

  // Global State
  const { baseUrl, configLoading, user } = useContext(UserContext);

  const [deleteId, setDeleteId] = useState(null);

  // Permission State
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [userBranch, setUserBranch] = useState("");
  const [userRole, setUserRole] = useState("");
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Filter state for dynamic branches
  const [filterBranches, setFilterBranches] = useState([]);
  const [selectedViewCompany, setSelectedViewCompany] = useState('');

  useEffect(() => {
    const fetchFilterBranches = async () => {
      try {
        const url = baseUrl || "";
        const comp = selectedViewCompany || localStorage.getItem('active_company');
        if (!comp || comp === 'All') {
          setFilterBranches(availableBranches); // fallback
          return;
        }
        const resp = await axios.get(`${url}/api/branches?company_name=${encodeURIComponent(comp)}`, { headers: getHeaders(null, comp) });
        setFilterBranches([...new Set(resp.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.name)).filter(Boolean))]);
      } catch (err) {
        console.error("Error fetching filter branches:", err);
      }
    };
    fetchFilterBranches();
  }, [selectedViewCompany, baseUrl, availableBranches]);

  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [companyBranchesMap, setCompanyBranchesMap] = useState({});
  const [selectedAddBranches, setSelectedAddBranches] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const extractCompanyNames = (companies) => {
    if (!Array.isArray(companies)) return [];
    return companies
      .map(c => (typeof c === 'string' ? c : c.company_name || c.name || ''))
      .filter(Boolean);
  };


  // Helper to get headers with tenancy
  const getHeaders = (branch = null, comp = null) => {
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    const activeContext = localStorage.getItem('active_company');
    const token = localStorage.getItem('token');

    // Authorization
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // STRICT Multi-Tenant Isolation:
    // 1. Resolve Company from active context or fallback to user default
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const defaultCompany = user.company_name || user.company;

    const resolvedCompany = comp || ((activeContext && activeContext !== 'All') ? activeContext : defaultCompany);
    if (resolvedCompany) headers['X-Company-Name'] = resolvedCompany;

    // 2. Resolve Branch from Filter or active context
    const branchToUse = branch || (user.branch_name || user.branch || null);
    if (branchToUse && branchToUse !== 'All Branches') headers['X-Branch-Name'] = branchToUse;

    return headers;
  };

  // Initial Data Fetching
  useEffect(() => {
    // Wait for config to load
    if (configLoading) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = baseUrl || "";
        await Promise.all([
          fetchShifts(url),
          fetchPermissions(url),
          fetchDoctype(url)
        ]);
      } catch (err) {
        console.error("Error in initial fetch:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchDoctype = async (currentBaseUrl) => {
      try {
        const url = `${currentBaseUrl}/api/doctypes/Schedule Master`;
        const response = await axios.get(url, { headers: getHeaders() });
        if (response.data && response.data.fields) {
          setMetadata(response.data);
          setDoctypeFields(response.data.fields);
        }
      } catch (err) {
        console.error("Error fetching Schedule Master DocType:", err);
      }
    };

    const fetchPermissions = async (currentBaseUrl) => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          const roleRaw = (user.role || "").toLowerCase();
          const role = roleRaw.replace(' ', '_');
          const branch = user.branch || user.branch_name || "";
          const company = user.company || user.company_name || "";

          setCompanyName(company);
          setUserBranch(branch);

          const adminRoles = ['admin', 'super_admin', 'superadmin', 'company_admin', 'group_admin', 'branch_admin'];
          const isAdmin = adminRoles.includes(role);
          setIsCompanyAdmin(['company_admin', 'admin', 'super_admin', 'superadmin', 'branch_admin'].includes(role));
          setIsGroupAdmin(['group_admin', 'super_admin', 'superadmin'].includes(role));
          setUserRole(role);

          if (isAdmin) {
            setCanWrite(true);
            setCanDelete(true);
          }

          const activeContext = localStorage.getItem('active_company');
          if (activeContext === 'All' || role === 'group_admin') {
            fetchCompanies(currentBaseUrl, role);
          } else {
            setSelectedCompanies([activeContext || company]);
            fetchBranches(currentBaseUrl, activeContext || company);
          }

          if (role) {
            const url = `${currentBaseUrl}/api/role-permissions?role=${role}`;
            const response = await axios.get(url, { headers: getHeaders() });
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'schedule_master');
            if (pagePerm) {
              setCanWrite(pagePerm.canWrite === true);
              setCanDelete(pagePerm.canDelete === true);
            }
          }
        }
      } catch (error) {
        console.warn("Error fetching permissions:", error);
      }
    };

    const fetchCompanies = async (currentBaseUrl, currentRole = null) => {
      try {
        const userStr = localStorage.getItem('user');
        const userObj = userStr ? JSON.parse(userStr) : {};
        const activeRole = currentRole || userRole || (userObj.role || "").toLowerCase();

        let comps = [];
        if (userObj.companies && Array.isArray(userObj.companies) && userObj.companies.length > 0) {
          comps = extractCompanyNames(userObj.companies);
        } else if (userObj.company_name) {
          comps = [userObj.company_name];
        }

        if (comps.length > 0) {
          setAllCompanies(comps);
          setCompanyOptions(comps);
          return;
        }

        const url = `${currentBaseUrl}/api/company-details`;
        const response = await axios.get(url, { headers: getHeaders() });
        const details = response.data.companyDetails || [];
        const names = details.map(d => d.restaurantName || d.company_name).filter(Boolean);
        setAllCompanies([...new Set(names)]);
        setCompanyOptions([...new Set(names)]);
      } catch (err) {
        console.error('Error fetching companies:', err);
      }
    };


    fetchData();
  }, [baseUrl, configLoading]);

  // NEW: Fetch Branches for Company Assignment
  const fetchBranches = async (currentBaseUrl = baseUrl, specificCompany = null) => {
    try {
      const comp = specificCompany || (selectedCompanies.length > 0 ? selectedCompanies[0] : companyName);
      if (!comp || comp === 'All') return;

      const url = `${currentBaseUrl}/api/branches?company_name=${encodeURIComponent(comp)}`;
      const response = await axios.get(url, { headers: getHeaders(null, comp) });
      const branchList = response.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.name)).filter(Boolean);
      setAvailableBranches([...new Set(branchList)]);
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };

  useEffect(() => {
    if (selectedCompanies.length === 0) {
      setAvailableBranches(availableBranches);
      return;
    }
    const fetchFormBranches = async () => {
      try {
        const branchPromises = selectedCompanies.map(comp => axios.get(`${baseUrl || ''}/api/branches?company_name=${encodeURIComponent(comp)}`));
        const results = await Promise.all(branchPromises);
        let branches = [];
        results.forEach(res => {
          branches = [...branches, ...res.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.name))];
        });
        setAvailableBranches([...new Set(branches.filter(Boolean))]);
      } catch (err) { console.error(err); }
    };
    fetchFormBranches();
  }, [selectedCompanies, baseUrl]);

  const [columnOrder, setColumnOrder] = useState([
    { key: "scheduleName", label: "Shift Name", align: "left" },
    { key: "company_name", label: "Companies", align: "left" },
    { key: "branch_name", label: "Branches", align: "left" },
    { key: "dateRange", label: "Date Range", align: "left" },
    { key: "workingDays", label: "Working Days", align: "left" },
    { key: "timeSlots", label: "Shift Times", align: "left" },
    { key: "specialDays", label: "Special Days", align: "left" },
    { key: "actions", label: "Actions", align: "center" },
  ]);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [selectedFieldToAdd, setSelectedFieldToAdd] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(0);
  const possibleColumns = [
    { key: "id", label: "ID", align: "left" },
    { key: "schedule_name", label: "Schedule Name", align: "left" },
    { key: "company_name", label: "Company", align: "left" },
    { key: "branch_name", label: "Branch", align: "left" },
    { key: "start_time", label: "Start Time", align: "left" },
    { key: "end_time", label: "End Time", align: "left" },
    { key: "working_days", label: "Working Days", align: "left" },
    { key: "special_days", label: "Special Days", align: "left" },
    { key: "is_active", label: "Status", align: "center" },
    { key: "actions", label: "Actions", align: "center" }
  ];

  const safeParse = (val, fallback = []) => {
    if (!val) return fallback;
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : fallback;
      } catch (e) {
        console.error("Error parsing JSON field:", e, val);
        return fallback;
      }
    }
    return fallback;
  };

  const fetchShifts = async () => {
    try {
      // Logic handled in parent effect for initial load
      // But we set loading here for manual refreshes

      const url = baseUrl ? `${baseUrl}/api/schedules` : '/api/schedules';
      const response = await axios.get(url, { headers: getHeaders() });

      // Parse potential JSON strings in array fields
      const parsedData = response.data.map(shift => ({
        ...shift,
        working_days: safeParse(shift.working_days),
        special_days: safeParse(shift.special_days)
      }));

      setShifts(parsedData);
      setError(null);
    } catch (err) {
      setError(`Failed to fetch shifts: ${err.message}`);
      console.error(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- New Constants & Helpers for Rule Master Logic ---
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const specialDayTypes = [
    { value: 'Holiday', label: 'Holiday (Full Leave)' },
    { value: 'Half-Day', label: 'Half Day (Custom Timing)' },
    { value: 'Extended', label: 'Extended Hours' },
    { value: 'Special-Shift', label: 'Special Shift (Replace with Another Shift)' }
  ];

  const toggleDay = (day) => {
    setFormData(prev => {
      const currentWorking = prev.working_days || [];
      const newWorking = currentWorking.includes(day)
        ? currentWorking.filter(d => d !== day)
        : [...currentWorking, day];
      // Auto-update weekly off (days not in newWorking)
      const newOff = daysOfWeek.filter(d => !newWorking.includes(d));
      return { ...prev, working_days: newWorking, weekly_off: newOff };
    });
  };

  const addSpecialDay = () => {
    setFormData(prev => ({
      ...prev,
      special_days: [...(prev.special_days || []), { date: '', type: '', description: '', start_time: '', end_time: '', extended_start: '', extended_end: '', shift_id: '', is_observed: false }]
    }));
  };

  const removeSpecialDay = (index) => {
    setFormData(prev => ({
      ...prev,
      special_days: prev.special_days.filter((_, i) => i !== index)
    }));
  };

  const addSlot = () => {
    setFormData(prev => ({
      ...prev,
      time_slots: [...(prev.time_slots || []), { start_time: '', end_time: '', is_overnight: false }]
    }));
  };

  const removeSlot = (index) => {
    if (formData.time_slots.length > 1) {
      setFormData(prev => ({
        ...prev,
        time_slots: prev.time_slots.filter((_, i) => i !== index)
      }));
    }
  };

  const updateSlot = (index, field, value) => {
    setFormData(prev => {
      const newSlots = [...prev.time_slots];
      newSlots[index][field] = value;
      return { ...prev, time_slots: newSlots };
    });
  };

  const updateSpecialDay = (index, field, value) => {
    setFormData(prev => {
      const newSpecial = [...prev.special_days];
      newSpecial[index][field] = value;
      if (field === 'type' && value === 'Holiday') {
        newSpecial[index].is_observed = true;
      }
      return { ...prev, special_days: newSpecial };
    });
  };

  const getConditionalFields = (item, index) => {
    switch (item.type) {
      case 'Holiday': return null;
      case 'Half-Day':

        return (
          <>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Start {use24Hour ? '(24h)' : '(12h)'}</label>
              <CustomTimePicker value={item.start_time} onChange={(val) => updateSpecialDay(index, 'start_time', val)} format24={use24Hour} placeholder="Start" />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>End {use24Hour ? '(24h)' : '(12h)'}</label>
              <CustomTimePicker value={item.end_time} onChange={(val) => updateSpecialDay(index, 'end_time', val)} format24={use24Hour} placeholder="End" />
            </div>
          </>
        );
      case 'Extended':

        return (
          <>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Ext Start {use24Hour ? '(24h)' : '(12h)'}</label>
              <CustomTimePicker value={item.extended_start} onChange={(val) => updateSpecialDay(index, 'extended_start', val)} format24={use24Hour} placeholder="Start" />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Ext End {use24Hour ? '(24h)' : '(12h)'}</label>
              <CustomTimePicker value={item.extended_end} onChange={(val) => updateSpecialDay(index, 'extended_end', val)} format24={use24Hour} placeholder="End" />
            </div>
          </>
        );
      case 'Special-Shift':

        return (
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Replacement Shift</label>
            <select value={item.shift_id} onChange={(e) => updateSpecialDay(index, 'shift_id', e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}>
              <option value="">-- Select Shift --</option>
              {shifts.map(s => (
                <option key={s.id || s._id} value={s.id || s._id}>{s.schedule_name}</option>
              ))}
            </select>
          </div>
        );
      default: return null;
    }
  };



  const normalizeScheduleData = (shift) => {
    if (!shift) return {};
    return {
      ...shift,
      working_days: safeParse(shift.working_days),
      special_days: safeParse(shift.special_days),
      time_slots: safeParse(shift.time_slots, [{ start_time: '', end_time: '', is_overnight: false }])
    };
  };

  const handleAddShift = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      setError("You do not have permission to add shifts.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!formData.schedule_name.trim()) {
      setError('Shift name is required.');
      return;
    }

    if (formData.time_slots.some(s => !s.start_time || !s.end_time)) {
      setError('Please fill in all start and end times.');
      return;
    }

    const activeContext = localStorage.getItem('active_company');
    if (activeContext === 'All' && isGroupAdmin && selectedCompanies.length === 0) {
      setError("Please select at least one company.");
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorMsgs = [];

    try {
      const activeCtx = localStorage.getItem('active_company') || 'All';
      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : {};
      const userComp = userData.company_name || userData.company || '';
      const companiesToProcess = activeCtx === 'All'
        ? selectedCompanies
        : [activeCtx || userComp || ''].filter(Boolean);
      const url = baseUrl ? `${baseUrl}/api/schedules` : '/api/schedules';

      const payload = {
        ...formData,
        company_names: companiesToProcess,
        branch_names: isSpecificBranchActive ? [(localStorage.getItem("active_branch") || "")] : selectedAddBranches
      };
      const headers = getHeaders();

      try {
        await axios.post(url, payload, { headers });
        successCount++;
      } catch (err) {
        errorMsgs.push(`Error: ${err.response?.data?.error || err.message}`);
      }

      if (successCount > 0) {
        setMessage(`Shift added successfully!`);
        setFormData({
          schedule_name: '', time_slots: [{ start_time: '', end_time: '', is_overnight: false }],
          working_days: [], start_date: '', end_date: '', special_days: [], is_active: true
        });
        setSelectedBranches([]);
        fetchShifts();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(`Failed to add shift: ${errorMsgs.join(", ")}`);
      }
    } catch (err) {
      setError(`An unexpected error occurred.`);
    } finally {
      setLoading(false);
    }
  };

  // Validate form then open assignment popup
  const handleCreateScheduleClick = () => {
    if (!formData.schedule_name.trim()) {
      setError('Shift name is required.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (formData.time_slots.some(s => !s.start_time || !s.end_time)) {
      setError('Please fill in all start and end times.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setShowSaveModal(true);
  };

  const handleConfirmAndSave = async () => {
    if (!canWrite) {
      setError("You do not have permission to save shifts.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const activeContext = localStorage.getItem('active_company');
    if (activeContext === 'All' && isGroupAdmin && selectedCompanies.length === 0) {
      setError("Please select at least one company.");
      return;
    }
    setLoading(true);
    try {
      const activeCtx = localStorage.getItem('active_company') || 'All';
      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : {};
      const userComp = userData.company_name || userData.company || '';
      const companiesToProcess = activeCtx === 'All'
        ? selectedCompanies
        : [activeCtx || userComp || ''].filter(Boolean);
      const payload = {
        ...formData,
        company_names: companiesToProcess,
        branch_names: isSpecificBranchActive ? [(localStorage.getItem("active_branch") || "")] : selectedAddBranches
      };

      if (editingId) {
        // UPDATE
        const url = baseUrl ? `${baseUrl}/api/schedules/bulk/${editingId}` : `/api/schedules/bulk/${editingId}`;
        await axios.put(url, payload, { headers: getHeaders() });
        setShowSaveModal(false);
        setMessage('Shift updated successfully!');
        setEditingId(null);
      } else {
        // CREATE
        const url = baseUrl ? `${baseUrl}/api/schedules` : '/api/schedules';
        await axios.post(url, payload, { headers: getHeaders() });
        setShowSaveModal(false);
        setMessage('Shift added successfully!');
      }

      setFormData({
        schedule_name: '', time_slots: [{ start_time: '', end_time: '', is_overnight: false }],
        working_days: [], start_date: '', end_date: '', special_days: [], is_active: true
      });
      setSelectedBranches([]);
      setSelectedAddBranches([]);
      fetchShifts();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(`Failed to save shift: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyToggle = (company) => {
    setSelectedCompanies(prev =>
      prev.includes(company) ? prev.filter(c => c !== company) : [...prev, company]
    );
  };

  const handleEditShift = (shift) => {
    const normalized = normalizeScheduleData(shift);
    setFormData({
      ...normalized,
      is_active: shift.is_active !== undefined ? shift.is_active : true
    });

    // Resolve Company & Branch assignments for editing
    if (shift.company_names && shift.company_names.length > 0) {
      setSelectedCompanies(shift.company_names);
    } else if (shift.company_name) {
      setSelectedCompanies(Array.isArray(shift.company_name) ? shift.company_name : [shift.company_name]);
    } else {
      setSelectedCompanies([]);
    }

    if (shift.branch_names) {
      const bArray = Array.isArray(shift.branch_names) ? shift.branch_names : [shift.branch_names];
      setSelectedBranches(bArray);
      setSelectedAddBranches(bArray);
    }

    setEditingId(shift.global_ref_id || shift.id || shift._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateShift = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      setError("You do not have permission to update shifts.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!formData.schedule_name.trim()) {
      setError('Schedule name is required.');
      return;
    }

    setLoading(true);
    try {
      const activeCtx = localStorage.getItem('active_company') || 'All';
      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : {};
      const userComp = userData.company_name || userData.company || '';
      const companiesToProcess = activeCtx === 'All'
        ? selectedCompanies
        : [activeCtx || userComp || ''].filter(Boolean);

      const url = baseUrl ? `${baseUrl}/api/schedules/bulk/${editingId}` : `/api/schedules/bulk/${editingId}`;
      const payload = {
        ...formData,
        company_names: companiesToProcess,
        branch_names: isSpecificBranchActive ? [(localStorage.getItem("active_branch") || "")] : selectedAddBranches
      };

      await axios.put(url, payload, { headers: getHeaders() });
      setMessage('Shift updated successfully!');
      setEditingId(null);
      setFormData({
        schedule_name: '', time_slots: [{ start_time: '', end_time: '', is_overnight: false }],
        working_days: [], start_date: '', end_date: '', special_days: [], is_active: true
      });
      setSelectedBranches([]);
      fetchShifts();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(`Failed to update schedule: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteShift = (shift) => {
    setDeleteId(shift);
  };

  const handleExecuteDelete = async () => {
    if (!canDelete) {
      setError("You do not have permission to delete shifts.");
      setTimeout(() => setError(null), 3000);
      setDeleteId(null);
      return;
    }
    if (!deleteId) return;
    try {
      const isBulk = !!deleteId.global_ref_id;
      const targetId = deleteId.global_ref_id || deleteId.id || deleteId._id;
      const endpoint = isBulk ? `/api/schedules/bulk/${targetId}` : `/api/schedules/${targetId}`;
      const url = baseUrl ? `${baseUrl}${endpoint}` : endpoint;

      await axios.delete(url, { headers: getHeaders() });
      setMessage('Shift deleted successfully!');
      setDeleteId(null);
      fetchShifts();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(`Failed to delete shift: ${err.response?.data?.error || err.message}`);
      setTimeout(() => setError(null), 3000);
      setDeleteId(null);
    }
  };

  const addColumn = () => {
    const fieldKey = selectedFieldToAdd;
    if (!fieldKey) return;
    const field = possibleColumns.find(p => p.key === fieldKey);
    if (!field || columnOrder.some(c => c.key === field.key)) return;
    const pos = parseInt(selectedPosition);
    const newOrder = [...columnOrder];
    newOrder.splice(pos, 0, field);
    setColumnOrder(newOrder);
    setSelectedFieldToAdd('');
    setSelectedPosition(0);
  };

  const removeColumn = (index) => {
    const newOrder = [...columnOrder];
    const removed = newOrder.splice(index, 1)[0];
    if (removed && removed.key === "actions") {
      newOrder.push(removed);
    }
    setColumnOrder(newOrder);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
    e.target.style.backgroundColor = 'rgba(52, 152, 219, 0.2)';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.target.style.backgroundColor = 'rgba(46, 204, 113, 0.2)';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
    const newOrder = [...columnOrder];
    const [draggedColumn] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);
    setColumnOrder(newOrder);
    document.querySelectorAll('table th').forEach(th => {
      th.style.backgroundColor = '';
    });
  };

  // Convert 12h string -> 24h string for display only
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '';
    if (!use24Hour) return timeStr; // Return 12h as is

    // Match "06:18 PM"
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
    if (match) {
      let h = parseInt(match[1]);
      let m = match[2];
      let p = match[3].toUpperCase();

      let { h: h24, m: m24 } = to24h(h, m, p);
      return `${h24}:${m24}`;
    }
    return timeStr;
  };

  const thStyle = {
    padding: '15px 12px',
    border: 'none',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    fontWeight: '600',
    fontSize: '0.95rem'
  };

  const tdStyle = {
    padding: '15px 12px',
    borderRight: '1px solid #e9ecef',
    whiteSpace: 'normal',
    color: '#2c3e50'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    border: '1px solid #3498db',
    borderRadius: '10px',
    background: '#f8f9fa',
    fontSize: '1rem',
    transition: 'all 0.2s',
    outline: 'none'
  };

  const getCellContent = (shift, col) => {
    const val = shift[col.key];

    if (col.key === 'actions') {

      return (
        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
          {canWrite && (
            <button className="action-btn action-btn-edit" onClick={() => handleEditShift(shift)} title="Edit">
              <FaEdit />
            </button>
          )}
          {canDelete && (
            <button className="action-btn action-btn-delete" onClick={() => confirmDeleteShift(shift)} title="Delete">
              <FaTrash />
            </button>
          )}
        </div>
      );
    }

    if (col.key === 'scheduleName' || col.key === 'schedule_name') {
      return shift.schedule_name || shift.scheduleName || val || '-';
    }

    if (col.key === 'dateRange' || (col.key === 'start_date' && !shift.end_date)) {
      if (shift.start_date && shift.end_date) {
        return `${shift.start_date} to ${shift.end_date}`;
      }
      return val || '-';
    }

    if (col.key === 'working_days' || col.key === 'workingDays') {
      const days = Array.isArray(shift.working_days) ? shift.working_days : safeParse(shift.working_days);
      return days && days.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {days.map(d => <span key={d} style={{ background: '#3498db', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>{d}</span>)}
        </div>
      ) : 'All Days';
    }

    if (col.key === 'timeSlots' || col.key === 'shift_times' || col.key === 'start_time' || col.key === 'end_time') {
      // Compatibility with both direct fields and old time_slots array
      if (shift.start_time && shift.end_time && col.key !== 'timeSlots' && col.key !== 'shift_times') {
        if (col.key === 'start_time') return formatTimeDisplay(shift.start_time);
        if (col.key === 'end_time') return formatTimeDisplay(shift.end_time);
      }

      const slots = Array.isArray(shift.time_slots) ? shift.time_slots : safeParse(shift.time_slots);
      if (slots && slots.length > 0) {

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {slots.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.85rem', color: '#2c3e50' }}>
                <FaClock style={{ color: '#3498db', fontSize: '0.75rem' }} />
                <span>{formatTimeDisplay(s.start_time)} - {formatTimeDisplay(s.end_time)}</span>
                {s.is_overnight && <FaMoon style={{ color: '#f1c40f', fontSize: '0.75rem' }} />}
              </div>
            ))}
          </div>
        );
      }

      if (shift.start_time && shift.end_time) {

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#2c3e50' }}>
            <FaClock style={{ color: '#3498db' }} /> {formatTimeDisplay(shift.start_time)} - {formatTimeDisplay(shift.end_time)}
            {shift.is_overnight && <FaMoon style={{ color: '#f1c40f', fontSize: '0.8rem' }} />}
          </div>
        );
      }
      return '-';
    }

    if (col.key === 'special_days' || col.key === 'specialDays') {
      const special = Array.isArray(shift.special_days) ? shift.special_days : safeParse(shift.special_days);
      return special && special.length > 0 ? `${special.length} Special Days` : '-';
    }

    if (col.key === 'is_active' || col.key === 'status') {
      return shift.is_active ? <span style={{ color: '#27ae60', fontWeight: 'bold' }}>Active</span> : <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>Inactive</span>;
    }
    if (col.key === 'company_name' || col.key === 'company') {
      const comps = shift.company_names && shift.company_names.length > 0 ? shift.company_names : (Array.isArray(shift.company_name) ? shift.company_name : [shift.company_name].filter(Boolean));
      return comps.length > 0 ? comps.join(", ") : '-';
    }

    if (col.key === 'branch_name' || col.key === 'branch' || col.key === 'branch_names') {
      const branches = Array.isArray(shift.branch_names) ? shift.branch_names : (shift.branch_name ? [shift.branch_name] : []);
      if (branches.length === 0 || (branches.length === 1 && branches[0] === 'All Branches')) {
        return 'All Branches';
      }
      return branches.join(", ");
    }

    return val || '-';
  };

  useEffect(() => {
    const fetchDynamicBranches = async () => {
      try {
        const url = baseUrl || "";
        const compsToFetch = selectedCompanies.length > 0 ? selectedCompanies : companyOptions;
        if (!compsToFetch || compsToFetch.length === 0) {
          return;
        }

        const localGetHeaders = () => {
          const token = localStorage.getItem('token');
          return token ? { Authorization: `Bearer ${token}` } : {};
        };
        const headersFunc = typeof getHeaders === 'function' ? getHeaders : localGetHeaders;

        const branchPromises = compsToFetch.map(comp =>
          axios.get(`${url}/api/branches?company_name=${encodeURIComponent(comp)}`, {
            headers: { ...headersFunc(), "X-Company-Name": comp }
          }).then(res => res.data)
        );

        const results = await Promise.all(branchPromises);
        const allBranches = [];
        results.forEach(data => {
          const branches = data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name)).filter(Boolean);
          allBranches.push(...branches);
        });

        setAvailableBranches([...new Set(allBranches)]);
      } catch (err) {
        console.error("Error fetching dynamic branches:", err);
      }
    };
    fetchDynamicBranches();
  }, [selectedCompanies, companyOptions, baseUrl]);
  if (loading) {

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)' }}>
        <div style={{ textAlign: 'center', color: '#3498db', fontSize: '18px' }}>
          <FaClock style={{ fontSize: '48px', marginBottom: '20px' }} />
          <p>Loading shifts...</p>
        </div>
      </div>
    );
  }


  const isGroupAdminRole = checkIsGlobalAdmin(JSON.parse(localStorage.getItem('user') || '{}'));
  const isAdminRole = checkIsAdmin(JSON.parse(localStorage.getItem('user') || '{}'));

  const activeCompanyLocal = (localStorage.getItem('active_company') || 'All').trim();
  const isSpecificCompanyActive = activeCompanyLocal.toLowerCase() !== 'all companies' && activeCompanyLocal.toLowerCase() !== 'all' && activeCompanyLocal.toLowerCase() !== 'global' && activeCompanyLocal !== '';
  const activeBranchLocal = (localStorage.getItem('active_branch') || 'All Branches').trim();
  const isSpecificBranchActive = activeBranchLocal.toLowerCase() !== 'all branches' && activeBranchLocal.toLowerCase() !== 'all' && activeBranchLocal !== '';

  const showCompanyAssign = !isSpecificBranchActive && (isGroupAdminRole || isAdminRole);
  const showBranchAssign = !isSpecificBranchActive && (isGroupAdminRole || isAdminRole);


  return (
    <div className="create-customer-container main-customer-page">

      {showAssignmentModal && (
        <div className="add-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="add-modal" style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '15px', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '15px', marginBottom: '20px' }}>
              <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Schedule Assignments</span>
              <button onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FaTimes /></button>
            </div>
            <div className="modal-body">
              {showCompanyAssign && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#3b82f6', marginBottom: '15px' }}><FaUsers style={{ marginRight: '8px' }} /> Select Company *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {companyOptions.map((comp, idx) => (
                      <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff6ff', borderRadius: '12px', border: '1.5px solid #dbeafe', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input
                          type="checkbox"
                          checked={selectedCompanies.includes(comp)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedCompanies(prev => [...prev, comp]);
                            else setSelectedCompanies(prev => prev.filter(c => c !== comp));
                          }}
                          style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
                        />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {showBranchAssign && (
                <div className="assignment-card">
                  <h4 style={{ color: '#10b981', marginBottom: '15px' }}><FaMapMarkerAlt style={{ marginRight: '8px' }} /> Select Branch</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {availableBranches.map((branchName, idx) => {
                      const bName = typeof branchName === 'string' ? branchName : (branchName.branch_name || branchName.name || branchName);

                      return (
                        <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#ecfdf5', borderRadius: '12px', border: '1.5px solid #d1fae5', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                          <input
                            type="checkbox"
                            checked={selectedAddBranches.includes(bName)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedAddBranches(prev => [...prev, bName]);
                              else setSelectedAddBranches(prev => prev.filter(b => b !== bName));
                            }}
                            style={{ width: '18px', height: '18px', accentColor: '#10b981' }}
                          />
                          {bName}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ borderTop: '1px solid #eee', paddingTop: '20px', marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAssignmentModal(false)} style={{ padding: '12px 25px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={() => navigate(-1)} className="header-back-btn" disabled={loading}>
            <FaArrowLeft /> Back
          </button>
        </div>

        <div className="header-center">
          <h1>Shift Master ({shifts.length})</h1>
        </div>

        <div className="header-right">
          <div className="header-actions">
            <button
              className="customize-btn"
              onClick={() => setShowColumnModal(true)}
              style={{ background: '#f1f5f9', color: '#475569', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '130px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease' }}
            >
              <FaCog /> Columns
            </button>
            {(isGroupAdmin || isCompanyAdmin || checkIsAdmin(JSON.parse(localStorage.getItem('user') || '{}'))) && (
              <button
                className="customize-btn"
                onClick={() => setShowCustomizeModal(true)}
                style={{ background: '#06D6A0', color: '#ffffff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '140px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(6, 214, 160, 0.2)' }}
              >
                <FaCogs /> Customize
              </button>
            )}
            {canWrite && (
              <button
                type="button"
                onClick={handleCreateScheduleClick}
                style={{ background: 'linear-gradient(135deg, #604BE8, #3b82f6)', color: '#ffffff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '170px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(96,75,232, 0.3)' }}
              >
                {editingId ? <><FaSave /> Update Schedule</> : <><FaPlus /> Create Schedule</>}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="header-divider"></div>

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper">

        {/* Premium Success/Error Modal */}
        {(message || error) && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999,
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div className="premium-modal-box" style={{
              background: '#ffffff',
              borderRadius: '24px',
              padding: '40px 32px',
              width: '90%',
              maxWidth: '440px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              animation: 'modalScaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background glowing accent */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '200px',
                height: '200px',
                background: message ? 'radial-gradient(circle, rgba(6, 214, 160, 0.15) 0%, rgba(255,255,255,0) 70%)' : 'radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, rgba(255,255,255,0) 70%)',
                zIndex: 0,
                pointerEvents: 'none'
              }}></div>

              {/* Icon Container */}
              <div className="premium-modal-icon" style={{
                width: '84px',
                height: '84px',
                borderRadius: '50%',
                background: message ? '#ecfdf5' : '#fef2f2',
                color: message ? '#06D6A0' : '#ef4444',
                border: `2px solid ${message ? '#a7f3d0' : '#fecaca'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '38px',
                marginBottom: '24px',
                boxShadow: message ? '0 10px 25px -5px rgba(6, 214, 160, 0.2)' : '0 10px 25px -5px rgba(239, 68, 68, 0.2)',
                zIndex: 1,
                animation: 'bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
              }}>
                {message ? <FaCheckCircle /> : <FaExclamationTriangle />}
              </div>

              {/* Title */}
              <h3 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: '#1e293b',
                margin: '0 0 12px 0',
                zIndex: 1,
                fontFamily: `'Gilroy', 'Inter', sans-serif`
              }}>
                {message ? "Success" : "Error"}
              </h3>

              {/* Message */}
              <p style={{
                fontSize: '16px',
                color: '#64748b',
                lineHeight: '1.6',
                margin: '0 0 32px 0',
                zIndex: 1,
                fontWeight: '500'
              }}>
                {message || error}
              </p>

              {/* Action Buttons */}
              <div className="premium-modal-actions" style={{
                display: 'flex',
                gap: '16px',
                width: '100%',
                zIndex: 1
              }}>
                <button 
                  type="button"
                  className="premium-btn-ok" 
                  onClick={() => {
                    if (message) setMessage('');
                    if (error) setError(null);
                  }} 
                  style={{
                    flex: 1,
                    height: '48px',
                    background: message ? '#06D6A0' : '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '14px',
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: 'pointer',
                    boxShadow: message ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(239, 68, 68, 0.3)',
                    transition: 'all 0.2s ease',
                    fontFamily: `'Gilroy', 'Inter', sans-serif`
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = message ? '0 12px 25px -5px rgba(6, 214, 160, 0.4)' : '0 12px 25px -5px rgba(239, 68, 68, 0.4)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = message ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(239, 68, 68, 0.3)'; }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="form-section-card theme-blue">
            <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div className="section-icon"><FaClock /></div>
                    <h2 style={{ margin: 0 }}>{editingId ? 'Edit Schedule' : 'Add New Schedule'}</h2>
                </div>
                {/* Format Toggle Button */}
                <div style={{ padding: '4px', background: '#f1f2f6', borderRadius: '8px', display: 'flex', gap: '4px', marginRight: '10px' }}>
                    <button
                    type="button"
                    onClick={() => setUse24Hour(false)}
                    style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        background: !use24Hour ? '#3b82f6' : 'transparent',
                        color: !use24Hour ? '#fff' : '#64748b',
                        transition: 'all 0.2s'
                    }}
                    >
                    12 Hour
                    </button>
                    <button
                    type="button"
                    onClick={() => setUse24Hour(true)}
                    style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        background: use24Hour ? '#3b82f6' : 'transparent',
                        color: use24Hour ? '#fff' : '#64748b',
                        transition: 'all 0.2s'
                    }}
                    >
                    24 Hour
                    </button>
                </div>
            </div>
            <div style={{ padding: '20px 0', position: 'relative', width: '100%' }}>



                    <form onSubmit={editingId ? handleUpdateShift : handleAddShift} style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>



            {/* Shift Name - Full Width */}
            <div style={{ gridColumn: '1 / -1', marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#2c3e50', fontSize: '0.95rem' }}>Shift Name</label>
              <input
                type="text"
                name="schedule_name"
                placeholder="e.g. Morning Shift"
                value={formData.schedule_name}
                onChange={handleInputChange}
                style={{ width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }}
                required
              />
            </div>

            {/* Shift Timings (Time Slots) Section */}
            <div style={{ gridColumn: '1 / -1', marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#2c3e50', fontSize: '0.95rem' }}>Shift Timings (Time Slots)</label>

              <div style={{ border: 'none', borderRadius: '12px', padding: '25px', background: 'white' }}>
                {formData.time_slots.map((slot, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '40px', marginBottom: index === formData.time_slots.length - 1 ? 0 : '25px', position: 'relative' }}>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ fontSize: '0.85rem', color: '#7f8c8d', marginBottom: '8px', display: 'block' }}>Start Time ({use24Hour ? '24h' : '12h'})</label>
                      <CustomTimePicker
                        value={slot.start_time}
                        onChange={(val) => updateSlot(index, 'start_time', val)}
                        format24={use24Hour}
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ fontSize: '0.85rem', color: '#7f8c8d', marginBottom: '8px', display: 'block' }}>End Time ({use24Hour ? '24h' : '12h'})</label>
                      <CustomTimePicker
                        value={slot.end_time}
                        onChange={(val) => updateSlot(index, 'end_time', val)}
                        format24={use24Hour}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginTop: '22px' }}>
                      <input
                        type="checkbox"
                        checked={slot.is_overnight}
                        onChange={(e) => updateSlot(index, 'is_overnight', e.target.checked)}
                        style={{ width: '20px', height: '20px' }}
                      />
                      <span style={{ fontSize: '1rem', color: '#2c3e50' }}>Overnight?</span>
                    </label>

                    {formData.time_slots.length > 1 && (
                      <button type="button" onClick={() => removeSlot(index)} style={{ padding: '6px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', position: 'absolute', right: '-15px', top: '0' }}>
                        <FaTimes size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addSlot}
                style={{
                  marginTop: '15px',
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '12px 28px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)',
                  fontSize: '0.95rem'
                }}
              >
                <FaPlus /> Add Another Slot
              </button>
            </div>

            {/* Date Range Section */}
            <div style={{ gridColumn: '1 / -1', marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#2c3e50', fontSize: '0.95rem' }}>Date Range</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <CustomDateTimePicker type="date" value={formData.start_date || ""} onChange={(val) => setFormData({ ...formData, start_date: val })} />
                <span style={{ color: '#7f8c8d', fontWeight: '600' }}>to</span>
                <CustomDateTimePicker type="date" value={formData.end_date || ""} onChange={(val) => setFormData({ ...formData, end_date: val })} />
              </div>
            </div>

            {/* Set Working Days Section */}
            <div style={{ gridColumn: '1 / -1', marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#2c3e50', fontSize: '0.95rem' }}>Set Working Days (Unselected days become Weekly Offs)</label>
              <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                {daysOfWeek.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    style={{
                      padding: '12px 28px',
                      borderRadius: '40px',
                      border: 'none',
                      background: formData.working_days.includes(day) ? '#1B65F2' : '#F3F6FB',
                      color: formData.working_days.includes(day) ? 'white' : '#1B1B29',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      minWidth: '80px',
                      fontSize: '0.95rem'
                    }}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <div style={{ marginTop: '12px', fontSize: '0.9rem', color: '#7f8c8d', fontWeight: '500' }}>
                <strong>Weekly Offs:</strong> {daysOfWeek.filter(d => !formData.working_days.includes(d)).length > 0
                  ? daysOfWeek.filter(d => !formData.working_days.includes(d)).join(", ")
                  : "None (7 days working)"}
              </div>
            </div>

            {/* Dynamic DocType Fields (Other than the ones styled above) */}
            {doctypeFields.filter(f => !['schedule_name', 'time_slots', 'working_days', 'start_date', 'end_date', 'is_active', 'is_overnight', 'start_time', 'end_time', 'hidden'].includes(f.id) && !f.hidden).map(field => (
              <div key={field.id} style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>
                  {field.label} {field.mandatory && <span style={{ color: '#e74c3c' }}>*</span>}
                </label>

                {field.type === 'Data' || field.type === 'Number' ? (
                  <input
                    type={field.type === 'Number' ? 'number' : 'text'}
                    name={field.id}
                    value={formData[field.id] || ''}
                    onChange={handleInputChange}
                    style={{ width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }}
                    placeholder={field.description || ''}
                  />
                ) : field.type === 'Time' ? (
                  <CustomTimePicker
                    value={formData[field.id]}
                    onChange={(val) => setFormData(p => ({ ...p, [field.id]: val }))}
                    format24={use24Hour}
                  />
                ) : field.type === 'Check' ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!formData[field.id]}
                      onChange={(e) => setFormData(p => ({ ...p, [field.id]: e.target.checked }))}
                    />
                    <span style={{ fontSize: '0.9rem', color: '#34495e' }}>{field.label}</span>
                  </label>
                ) : field.type === 'MultiSelect' ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {(field.options || daysOfWeek).map(opt => (
                      <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px 12px', background: (formData[field.id] || []).includes(opt) ? '#3498db' : '#f8f9fa', color: (formData[field.id] || []).includes(opt) ? 'white' : '#34495e', borderRadius: '20px', fontSize: '0.85rem', transition: 'all 0.2s' }}>
                        <input
                          type="checkbox"
                          hidden
                          checked={(formData[field.id] || []).includes(opt)}
                          onChange={() => {
                            const current = formData[field.id] || [];
                            const next = current.includes(opt) ? current.filter(o => o !== opt) : [...current, opt];
                            setFormData(p => ({ ...p, [field.id]: next }));
                          }}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}

            {/* Active Status Toggle */}
            <div style={{ gridColumn: '1 / -1', marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '10px 15px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #e0e6ed', width: 'fit-content' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold', color: '#2c3e50' }}>Active (Schedule is visible and in use)</span>
              </label>
            </div>

            {/* Special Days Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <label style={{ fontWeight: '600', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaCalendarAlt style={{ color: '#3498db' }} /> Special Days (Holidays / Exceptions)
                </label>
                <button type="button" onClick={addSpecialDay} style={{ padding: '6px 15px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer' }}>
                  <FaPlus style={{ marginRight: '5px' }} /> Add Date
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {formData.special_days.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '15px', background: '#ffffff', padding: '15px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 5px rgba(0,0,0,0.02)' }}>
                    <div style={{ flex: 1 }}>
                      <CustomDateTimePicker type="date" value={item.date || ""} onChange={(val) => updateSpecialDay(idx, "date", val)} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <select value={item.type} onChange={(e) => updateSpecialDay(idx, 'type', e.target.value)} style={{ width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }} required>
                        <option value="">-- Select Type --</option>
                        {specialDayTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <input type="text" placeholder="Reason" value={item.description} onChange={(e) => updateSpecialDay(idx, 'description', e.target.value)} style={{ width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }} required />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>{getConditionalFields(item, idx)}</div>
                    <button type="button" onClick={() => removeSpecialDay(idx)} style={{ color: '#e74c3c', background: 'rgba(231,76,60,0.1)', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 }} title="Remove"><FaTimes size={14} /></button>
                  </div>
                ))}
              </div>
            </div>


            {editingId && (
              <button type="button" onClick={() => { setEditingId(null); setFormData({ schedule_name: '', start_time: '', end_time: '', is_overnight: false, working_days: [], special_days: [], is_active: true }); setSelectedBranches([]); }} style={{ width: '100%', padding: '12px', background: '#f1f5f9', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer' }}>
                Cancel Edit
              </button>
            )}
          </form>
          </div>
        </div>

        {/* --- Shift Table --- */}
        <div className="form-section-card theme-purple">
            <div className="section-header" style={{ justifyContent: 'flex-start', gap: '15px' }}>
                <div className="section-icon"><FaClock /></div>
                <h2>Shifts List</h2>
            </div>
        <div style={{ background: '#ffffff', borderRadius: '15px', overflow: 'hidden', border: '1px solid #e9ecef', margin: '20px' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="premium-table">
              <thead>
                <tr>
                  {columnOrder.map((col, index) => (
                    <th
                      key={col.key}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      style={{ cursor: 'move' }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shifts.length > 0 ? (
                  shifts.map((shift, index) => (
                    <tr key={shift.id || shift._id}>
                      {columnOrder.map(col => (
                        <td key={col.key} style={{ textAlign: col.align || 'left' }}>
                          {getCellContent(shift, col)}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columnOrder.length} style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
                      No shifts found. Create one above!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>

      </div>
      </div>

      {/* ===== Create Schedule Assignment Modal ===== */}
      {showSaveModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2500, animation: 'fadeIn 0.3s ease' }}>
          <div style={{ background: '#ffffff', borderRadius: '28px', width: '95%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 70px -15px rgba(15,23,42,0.35)', animation: 'modalPop 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
            {/* Modal Header */}
            <div style={{ background: 'linear-gradient(135deg, #604BE8, #3b82f6)', padding: '28px 32px', borderRadius: '28px 28px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '44px', height: '44px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#fff' }}>
                  <FaBuilding />
                </div>
                <div>
                  <h2 style={{ margin: 0, color: '#fff', fontSize: '20px', fontWeight: '800' }}>Assign & Save Schedule</h2>
                  <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.75)', fontSize: '13px', fontWeight: '500' }}>"{formData.schedule_name}"</p>
                </div>
              </div>
              <button onClick={() => setShowSaveModal(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '18px', transition: 'all 0.2s' }}>
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '32px' }}>

              {/* Assign to Companies */}
              {(showCompanyAssign || isGroupAdmin) && companyOptions.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                  <h3 style={{ margin: '0 0 16px', color: '#1e293b', fontSize: '16px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', width: '28px', height: '28px', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '13px' }}><FaBuilding /></span>
                    Assign to Companies <span style={{ color: '#ef4444' }}>*</span>
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                    {companyOptions.map((comp, idx) => {
                      const isSel = selectedCompanies.includes(comp);
                      return (
                        <div key={idx}
                          onClick={() => {
                            if (isSel) setSelectedCompanies(prev => prev.filter(c => c !== comp));
                            else setSelectedCompanies(prev => [...prev, comp]);
                          }}
                          style={{ border: isSel ? '2px solid #3b82f6' : '2px solid #e2e8f0', padding: '14px 16px', borderRadius: '14px', background: isSel ? '#eff6ff' : '#f8fafc', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '12px' }}
                        >
                          <div style={{ width: '20px', height: '20px', border: isSel ? '2px solid #3b82f6' : '2px solid #cbd5e1', borderRadius: '6px', background: isSel ? '#3b82f6' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                            {isSel && <FaCheckCircle style={{ color: '#fff', fontSize: '12px' }} />}
                          </div>
                          <span style={{ fontWeight: '700', color: isSel ? '#1d4ed8' : '#475569', fontSize: '14px' }}>{comp}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Assign to Branches */}
              {availableBranches.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                  <h3 style={{ margin: '0 0 16px', color: '#1e293b', fontSize: '16px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ background: 'linear-gradient(135deg, #10b981, #34d399)', width: '28px', height: '28px', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '13px' }}><FaMapMarkerAlt /></span>
                    Assign to Branches
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                    {availableBranches.map((brName, idx) => {
                      const isSel = selectedAddBranches.includes(brName);
                      return (
                        <div key={idx}
                          onClick={() => {
                            if (isSel) setSelectedAddBranches(prev => prev.filter(b => b !== brName));
                            else setSelectedAddBranches(prev => [...prev, brName]);
                          }}
                          style={{ border: isSel ? '2px solid #10b981' : '2px solid #e2e8f0', padding: '14px 16px', borderRadius: '14px', background: isSel ? '#ecfdf5' : '#f8fafc', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '12px' }}
                        >
                          <div style={{ width: '20px', height: '20px', border: isSel ? '2px solid #10b981' : '2px solid #cbd5e1', borderRadius: '6px', background: isSel ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                            {isSel && <FaCheckCircle style={{ color: '#fff', fontSize: '12px' }} />}
                          </div>
                          <span style={{ fontWeight: '700', color: isSel ? '#065f46' : '#475569', fontSize: '14px' }}>{brName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No companies/branches info */}
              {companyOptions.length === 0 && availableBranches.length === 0 && (
                <div style={{ background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: '14px', padding: '20px', textAlign: 'center', color: '#0369a1' }}>
                  <FaBuilding style={{ fontSize: '24px', marginBottom: '8px' }} />
                  <p style={{ margin: 0, fontWeight: '600' }}>Schedule will be saved to your current company/branch.</p>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '14px', marginTop: '8px' }}>
                <button
                  onClick={() => setShowSaveModal(false)}
                  style={{ flex: 1, height: '52px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '16px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAndSave}
                  disabled={loading}
                  style={{ flex: 2, height: '52px', background: 'linear-gradient(135deg, #604BE8, #3b82f6)', color: '#fff', border: 'none', borderRadius: '16px', fontWeight: '700', fontSize: '15px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 8px 20px -6px rgba(96,75,232,0.4)', opacity: loading ? 0.7 : 1, transition: 'all 0.2s' }}
                >
                  {loading ? '⏳ Saving...' : <><FaSave /> Confirm & Save Schedule</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}

      {deleteId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '15px', width: '90%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            <FaExclamationTriangle style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }} />
            <h3 style={{ color: '#2c3e50', margin: '0 0 15px 0' }}>Confirm Deletion</h3>
            <p style={{ color: '#7f8c8d', marginBottom: '25px' }}>Are you sure you want to delete this shift? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button onClick={handleExecuteDelete} style={{ padding: '10px 25px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer' }}>
                Yes, Delete
              </button>
              <button onClick={() => setDeleteId(null)} style={{ padding: '10px 25px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Manager Modal */}
      {showColumnModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200 }}>
          <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '15px', width: '90%', maxWidth: '500px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>Manage Table Columns</h3>
              <FaTimes style={{ cursor: 'pointer', color: '#7f8c8d' }} onClick={() => setShowColumnModal(false)} />
            </div>

            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#34495e', fontSize: '0.9rem' }}>Add New Column</h4>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select
                  value={selectedFieldToAdd}
                  onChange={(e) => setSelectedFieldToAdd(e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '5px', border: '1px solid #bdc3c7' }}
                >
                  <option value="">Select Field...</option>
                  {possibleColumns.filter(p => !columnOrder.some(c => c.key === p.key)).map(p => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
                <select
                  value={selectedPosition}
                  onChange={(e) => setSelectedPosition(e.target.value)}
                  style={{ width: '80px', padding: '8px', borderRadius: '5px', border: '1px solid #bdc3c7' }}
                >
                  {columnOrder.map((_, i) => (
                    <option key={i} value={i}>Pos {i + 1}</option>
                  ))}
                  <option value={columnOrder.length}>Last</option>
                </select>
                <button
                  onClick={addColumn}
                  disabled={!selectedFieldToAdd}
                  style={{ padding: '8px 15px', background: '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: selectedFieldToAdd ? 'pointer' : 'not-allowed' }}
                >
                  Add
                </button>
              </div>
            </div>

            <div>
              <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#34495e', fontSize: '0.9rem' }}>Current Columns (Drag to Reorder)</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {columnOrder.map((col, index) => (
                  <div
                    key={col.key}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    style={{
                      padding: '10px',
                      background: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '5px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'move',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                  >
                    <span>{index + 1}. {col.label}</span>
                    <FaTimes
                      style={{ color: '#e74c3c', cursor: 'pointer' }}
                      title="Remove"
                      onClick={() => removeColumn(index)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button onClick={() => setShowColumnModal(false)} style={{ padding: '10px 20px', background: '#34495e', color: 'white', border: 'none', borderRadius: '50px', cursor: 'pointer' }}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={() => window.location.reload()}
        targetDocType="Schedule Master"
      />
    </div>
  );
};

export default ScheduleMaster;
